import { ChevronDown, MapPin, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontData } from '@/features/storefront/data/context';
import { PIN_CODE } from '@/features/storefront/delivery';
import { checkPincode, useDeliveryPincode } from '@/features/storefront/hooks/useDeliveryPincode';
import { CHROME_PANEL_STYLE } from '@/features/storefront/layouts/chrome/types';
import type { StorefrontTheme } from '@/features/storefront/themeSchema';
import { cn } from '@/lib/cn';

/**
 * The header's "Deliver to <pincode>" control (`header.location_selector`), as
 * grocery and quick-commerce shops show it between the logo and the search.
 *
 * The PIN code is the one the product page's delivery check remembers
 * (`useDeliveryPincode`), so typing it in either place shows in both. The
 * check asks the shop's own delivery quote whether it delivers there at all.
 *
 * Two placements: `inline`, the compact button a search-led masthead sets
 * beside its logo from the large breakpoint up; and `bar`, a thin line under
 * the masthead - on phones always, and on wider screens too for the headers
 * that have no room of their own for it (`wide`).
 *
 * In the customiser (`preview`) nothing is asked of the server or remembered:
 * the dashboard is not the shop's host. A check there just shows what a
 * shopper would read.
 */

type Answer =
  | { kind: 'idle' }
  | { kind: 'loading'; pincode: string }
  | { kind: 'error'; pincode: string }
  | { kind: 'done'; pincode: string; serviceable: boolean; message: string | null };

export function HeaderLocationSelector({
  header,
  preview,
  placement,
  wide = false,
}: {
  header: StorefrontTheme['header'];
  preview?: boolean;
  placement: 'inline' | 'bar';
  /** For `bar`: shown at every width, because the masthead has no inline one. */
  wide?: boolean;
}) {
  if (!header.location_selector) return null;
  return <LocationControl preview={preview} placement={placement} wide={wide} />;
}

function LocationControl({
  preview,
  placement,
  wide,
}: {
  preview?: boolean;
  placement: 'inline' | 'bar';
  wide: boolean;
}) {
  const text = useShopContent();
  const source = useStorefrontData();
  const [saved, save] = useDeliveryPincode(source.key, !preview);
  const [previewPincode, setPreviewPincode] = useState('');
  const pincode = preview ? previewPincode : saved;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [answer, setAnswer] = useState<Answer>({ kind: 'idle' });
  const self = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const asked = useRef(0);
  const panelId = useId();
  const headingId = useId();
  const inputId = useId();

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const away = (event: PointerEvent) => {
      if (!self.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const toggle = () => {
    if (open) {
      close(false);
      return;
    }
    setDraft(pincode);
    setInvalid(false);
    setOpen(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const next = draft.trim();
    if (!PIN_CODE.test(next)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (preview) {
      setPreviewPincode(next);
      setAnswer({ kind: 'done', pincode: next, serviceable: true, message: null });
      return;
    }
    save(next);
    const ask = ++asked.current;
    setAnswer({ kind: 'loading', pincode: next });
    try {
      const quote = await checkPincode(source, next);
      if (ask !== asked.current) return;
      setAnswer({ kind: 'done', pincode: next, serviceable: quote.serviceable, message: quote.message });
    } catch {
      if (ask !== asked.current) return;
      setAnswer({ kind: 'error', pincode: next });
    }
  };

  const label = pincode ? text('chrome.deliver_to', { pincode }) : text('chrome.set_pincode');
  const bar = placement === 'bar';

  let result = null;
  if (answer.kind === 'loading') {
    result = (
      <p className="mt-3" style={{ color: 'var(--storefront-muted)' }}>
        {text('checkout.delivery_loading')}
      </p>
    );
  } else if (answer.kind === 'error') {
    result = (
      <p role="alert" className="mt-3">
        {text('checkout.delivery_error')}
      </p>
    );
  } else if (answer.kind === 'done') {
    result = (
      <p role="status" className="mt-3 font-medium" data-serviceable={answer.serviceable ? 'yes' : 'no'}>
        {answer.serviceable
          ? text('chrome.we_deliver', { pincode: answer.pincode })
          : text('chrome.no_delivery', { pincode: answer.pincode })}
      </p>
    );
  }

  const control = (
    <div
      ref={self}
      className={cn('relative', !bar && 'shrink-0')}
      data-location-selector={placement}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          close(true);
        }
      }}
    >
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-haspopup="dialog"
        onClick={toggle}
        className={cn(
          'flex items-center gap-1.5 text-sm hover:opacity-80',
          bar ? 'py-1.5 text-xs' : 'max-w-[11rem]',
        )}
      >
        <MapPin className="size-4 shrink-0" aria-hidden />
        <span className={cn('truncate', pincode && 'font-semibold')}>{label}</span>
        <ChevronDown className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-labelledby={headingId}
          className="absolute start-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-2rem)] border p-4 text-sm shadow-xl"
          style={{ ...CHROME_PANEL_STYLE, borderRadius: 'var(--storefront-radius)' }}
        >
          <div className="flex items-start justify-between gap-3">
            <label id={headingId} htmlFor={inputId} className="font-semibold">
              {text('chrome.pincode_heading')}
            </label>
            <button
              type="button"
              aria-label={text('chrome.pincode_close')}
              onClick={() => close(true)}
              className="-m-1 p-1 hover:opacity-70"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
          <form className="mt-3 flex gap-2" onSubmit={submit} noValidate>
            <input
              ref={input}
              id={inputId}
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={6}
              value={draft}
              placeholder={text('product_page.pincode_placeholder')}
              aria-invalid={invalid || undefined}
              onChange={(event) => setDraft(event.target.value.replace(/\D/g, '').slice(0, 6))}
              className="h-10 min-w-0 flex-1 border bg-transparent px-3 text-sm"
              style={{ borderColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
            />
            <button
              type="submit"
              className="h-10 shrink-0 px-4 text-sm font-semibold text-white"
              style={{ backgroundColor: 'var(--storefront-primary)', borderRadius: 'var(--storefront-radius)' }}
            >
              {text('product_page.pincode_check')}
            </button>
          </form>
          {invalid ? (
            <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--storefront-primary)' }}>
              {text('product_page.pincode_invalid')}
            </p>
          ) : (
            result
          )}
        </div>
      ) : null}
    </div>
  );

  if (!bar) return <div className="hidden items-center lg:flex preview-narrow:hidden!">{control}</div>;
  return (
    <div
      data-location-bar=""
      className={cn('border-b', wide ? 'flex' : 'flex lg:hidden preview-narrow:flex!')}
      style={{
        borderColor: 'var(--storefront-surface)',
        backgroundColor: 'var(--storefront-background)',
        color: 'var(--storefront-text)',
      }}
    >
      <StorefrontContainer className="flex w-full">{control}</StorefrontContainer>
    </div>
  );
}
